import React, { createContext, useContext, useEffect, useRef, useState, useCallback, ReactNode } from 'react';
import { io } from 'socket.io-client';
import type { Socket } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { SocketContextType, SocketUser, ChatMessage, PlayerBid } from '../types';
import toast from 'react-hot-toast';

const SocketContext = createContext<SocketContextType | undefined>(undefined);

interface SocketProviderProps {
  children: ReactNode;
}

const playChatNotificationTone = () => {
  try {
    const chatSoundEnabled = localStorage.getItem('chatSoundEnabled');
    if (chatSoundEnabled === 'false') {
      return;
    }

    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextClass) return;

    const context = new AudioContextClass();
    const oscillator = context.createOscillator();
    const gain = context.createGain();

    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(880, context.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(660, context.currentTime + 0.12);

    gain.gain.setValueAtTime(0.0001, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.08, context.currentTime + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.16);

    oscillator.connect(gain);
    gain.connect(context.destination);

    oscillator.start();
    oscillator.stop(context.currentTime + 0.16);

    oscillator.onended = () => {
      context.close().catch(() => {});
    };
  } catch (error) {
    // Ignore audio notification failures (browser policy/device restrictions)
  }
};

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [activeUsers, setActiveUsers] = useState<SocketUser[]>([]);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const { user, token, isAuthenticated } = useAuth();
  const currentDraftIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (isAuthenticated && token && user) {
      // Initialize socket connection
      const newSocket = io(process.env.REACT_APP_SERVER_URL || 'http://localhost:5000', {
        transports: ['websocket', 'polling'],
        timeout: 10000,
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
      });

      // Connection event handlers
      newSocket.on('connect', () => {
        console.log('Socket connected:', newSocket.id);
        setIsConnected(true);
        
        // Authenticate socket connection
        newSocket.emit('authenticate', { token });
      });

      newSocket.on('disconnect', () => {
        console.log('Socket disconnected');
        setIsConnected(false);
        setActiveUsers([]);
      });

      newSocket.on('connect_error', (error) => {
        console.error('Socket connection error:', error);
        setIsConnected(false);
        toast.error('Connection error. Please refresh the page.');
      });

      // Authentication event handlers
      newSocket.on('authenticated', (data) => {
        console.log('Socket authenticated:', data.message);
        toast.success('Connected to live draft');

        if (currentDraftIdRef.current) {
          newSocket.emit('joinDraft', { draftId: currentDraftIdRef.current });
        }
      });

      newSocket.on('authError', (data) => {
        console.error('Socket auth error:', data.message);
        toast.error('Authentication failed. Please refresh the page.');
      });

      // Draft room event handlers
      newSocket.on('draftJoined', (data) => {
        console.log('Joined draft:', data.message);
        // Clear chat messages when joining a new draft
        setChatMessages([]);
        // Only show toast once per session to avoid spam
        if (!sessionStorage.getItem(`draft-joined-${data.draftId}`)) {
          toast.success('Joined draft room');
          sessionStorage.setItem(`draft-joined-${data.draftId}`, 'true');
        }
      });

      newSocket.on('userJoined', (data) => {
        toast.success(`${data.user.username} joined the draft`);
      });

      newSocket.on('userLeft', (data) => {
        toast(`${data.user.username} left the draft`, {
          icon: '👋',
        });
      });

      newSocket.on('activeUsersUpdate', (data) => {
        setActiveUsers(data.activeUsers);
      });

      // Draft event handlers
      newSocket.on('draftStarted', (data) => {
        toast.success('Draft has started!', {
          duration: 5000,
          icon: '🏈',
        });
      });

      newSocket.on('draftUpdated', (data) => {
        // Handle draft updates
        console.log('Draft updated:', data.draft);
      });

      newSocket.on('pickMade', (data) => {
        const { pick, team } = data;
        toast.success(
          `${team.name} drafted ${pick.playerName} (${pick.position}) for $${pick.cost}`,
          {
            duration: 5000,
            icon: '⚡',
          }
        );
      });

      newSocket.on('pickUndone', (data) => {
        toast(`Pick #${data.pickNumber} has been undone`, {
          icon: '↩️',
        });
      });

      newSocket.on('draftCompleted', (data) => {
        toast.success('Draft completed! 🎉', {
          duration: 10000,
        });
      });

      // Player update event for available players refresh
      newSocket.on('playerUpdated', (data) => {
        // Emit a custom event that components can listen to
        window.dispatchEvent(new CustomEvent('playerUpdated', { detail: data }));
      });

      // Bidding event handlers
      newSocket.on('playerBid', (data: PlayerBid) => {
        // Handle player bid notifications
        console.log('Player bid:', data);
      });

      // Chat event handlers
      newSocket.on('chatMessage', (data: ChatMessage) => {
        // Handle chat messages
        console.log('Chat message:', data);
        setChatMessages(prev => [...prev, data]);

        const fromCurrentUser = data?.user?.username === user?.username;
        if (!fromCurrentUser) {
          const sender = data?.user?.username || 'Someone';
          const preview = data?.message ? `: ${data.message.slice(0, 80)}` : '';

          toast(`${sender}${preview}`, {
            icon: '💬',
            duration: 3000,
          });

          playChatNotificationTone();
        }
      });

      newSocket.on('userTyping', (data) => {
        // Handle typing indicators
        console.log('User typing:', data);
      });

      // Error handler
      newSocket.on('error', (data) => {
        console.error('Socket error:', data.message);
        toast.error(data.message);
      });

      setSocket(newSocket);

      return () => {
        newSocket.close();
        setSocket(null);
        setIsConnected(false);
        setActiveUsers([]);
        setChatMessages([]);
      };
    }
  }, [isAuthenticated, token, user]);

  const joinDraft = useCallback((draftId: string) => {
    currentDraftIdRef.current = draftId;
    if (socket && isConnected) {
      socket.emit('joinDraft', { draftId });
    }
  }, [socket, isConnected]);

  const leaveDraft = useCallback((draftId: string) => {
    if (currentDraftIdRef.current === draftId) {
      currentDraftIdRef.current = null;
    }
    if (socket && isConnected) {
      socket.emit('leaveDraft', { draftId });
    }
  }, [socket, isConnected]);

  const sendChatMessage = useCallback((draftId: string, message: string) => {
    if (socket && isConnected && message.trim()) {
      socket.emit('chatMessage', { draftId, message: message.trim() });
    }
  }, [socket, isConnected]);

  const bidOnPlayer = useCallback((draftId: string, playerId: string, bidAmount: number) => {
    if (socket && isConnected) {
      socket.emit('bidOnPlayer', { draftId, playerId, bidAmount });
    }
  }, [socket, isConnected]);

  const startTyping = (draftId: string) => {
    if (socket && isConnected) {
      socket.emit('typing', { draftId, isTyping: true });
    }
  };

  const stopTyping = (draftId: string) => {
    if (socket && isConnected) {
      socket.emit('typing', { draftId, isTyping: false });
    }
  };

  const value: SocketContextType = {
    socket,
    isConnected,
    activeUsers,
    chatMessages,
    joinDraft,
    leaveDraft,
    sendChatMessage,
    bidOnPlayer,
    startTyping,
    stopTyping,
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = (): SocketContextType => {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export default SocketContext;
